/** @format */

import {ApolloClient, InMemoryCache, ApolloLink, HttpLink, split, Observable} from '@apollo/client';
import {startTiming} from '../diagnostics/performance';
import {WebSocketLink} from '@apollo/client/link/ws';
import {onError} from '@apollo/client/link/error';
import {setContext} from '@apollo/client/link/context';
import {getMainDefinition} from '@apollo/client/utilities';
import {captureMessage} from '@sentry/browser';
import LoginHandler from './auth';
import {resolvers} from './resolvers';
import {SIDE_NAVS} from './query/SideNav';
import {ALL_ASSETS_MEM, HISTORY_POLYLINES_SETTING} from './query/AssetQuery';
import {ALL_HISTORY_LOADING} from './query/HistoryButton';
import {makeVar} from '@apollo/client';
import {trimLatLngGrid} from './cacheUtils';
import {isAuthenticationError} from './authErrors';

const httpURL = 'https://gql.gpson.cz:3000/graphql';
const wssURL = 'wss://gql.gpson.cz:3000/subscriptions';

const cache = new InMemoryCache({
  typePolicies: {
    LatLng: {
      keyFields: false,
    },
    LatLngMem: {
      keyFields: false,
    },
    HistoryPolylinesSetting: {
      keyFields: ['assetId', 'startUtime', 'stopUtime'],
      fields: {
        historyPolylinesSetting: {
          merge(_existing, incoming) {
            return incoming;
          },
        },
      },
    },
    AssetInMem: {
      keyFields: ['id'],
      fields: {
        latLngGrid: {
          merge(_existing, incoming) {
            return trimLatLngGrid(incoming);
          },
        },
      },
    },
    Group: {
      keyFields: ['name'],
      fields: {
        tasks: {
          merge(_existing, incoming) {
            return incoming;
          },
        },
      },
    },
    Query: {
      fields: {
        assetInMem(existingData, {args, toReference}) {
          return existingData || toReference({__typename: 'AssetInMem', id: args.id});
        },
        historyPolylinesSetting: {
          merge(_existing, incoming) {
            return incoming;
          },
        },
        assetsInMem: {
          merge(_existing, incoming) {
            return incoming;
          },
        },
      },
    },
  },
});

const loginHandler = new LoginHandler();

const errorLink = onError(({graphQLErrors, networkError}) => {
  if (isAuthenticationError(graphQLErrors) || isAuthenticationError(networkError)) {
    loginHandler.logout({reason:'session-expired'});
    return;
  }
  if (graphQLErrors) {
    graphQLErrors.forEach(({message, locations, path}) => {
      console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`);
      captureMessage(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`);
    });
  }

  if (networkError) {
    console.log(`[Network error]: ${networkError}`);
    captureMessage(`[Network error]: ${networkError}`);
  }
});

const authLink = setContext((_, {headers}) => {
  const token = loginHandler.getAccessToken({reset: false});
  return {
    headers: {
      ...headers,
      ...(token
        ? {
            authorization: `Bearer ${token}`,
            authToken: token,
          }
        : {}),
    },
  };
});

const httpLink = new HttpLink({
  uri: httpURL,
  fetch: (uri,options)=>{
    const finish=startTiming('http.headers','graphql');
    return fetch(uri,options).then(response=>{finish(response.ok?'ok':'http_error');return response;},error=>{finish('error');throw error;});
  },
});

const performanceLink = new ApolloLink((operation,forward)=>new Observable(observer=>{
  const definition=getMainDefinition(operation.query);
  const name=(operation.operationName || definition.selectionSet?.selections?.[0]?.name?.value || 'anonymous').replace(/[^A-Za-z0-9_]/g,'').slice(0,64);
  const finish=startTiming('graphql',name);
  const subscription=forward(operation).subscribe({
    next:result=>observer.next(result),
    error:error=>{finish('error');observer.error(error);},
    complete:()=>{finish();observer.complete();},
  });
  return ()=>{finish('cancelled');subscription.unsubscribe();};
}));

const wsLink = new WebSocketLink({
  uri: wssURL,
  options: {
    reconnect: true,
    lazy: true,
    connectionParams: () => ({
      authToken: loginHandler.getAccessToken({reset: false}),
    }),
  },
});

const authMiddleware = {
  applyMiddleware(options, next) {
    options.authToken = loginHandler.getAccessToken({reset: false});
    next();
  },
};
wsLink.subscriptionClient.use([authMiddleware]);

// Large bootstrap queries over HTTP; live updates stay on WebSocket
const splitLink = split(
  ({query}) => {
    const definition = getMainDefinition(query);
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
  },
  wsLink,
  performanceLink.concat(authLink).concat(httpLink)
);

const client = new ApolloClient({
  link: ApolloLink.from([errorLink, splitLink]),
  cache,
  resolvers,
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    },
  },
});

const writeDefaults = () => {
  client.writeQuery({
    query: SIDE_NAVS,
    data: {
      sideNavs: [],
    },
  });
  client.writeQuery({
    query: ALL_ASSETS_MEM,
    data: {
      assetsInMem: [],
    },
  });
  client.writeQuery({
    query: HISTORY_POLYLINES_SETTING,
    data: {
      historyPolylinesSetting: [],
    },
  });
  client.writeQuery({
    query: ALL_HISTORY_LOADING,
    data: {
      historyButtonLoader: [],
    },
  });
};

writeDefaults();

client.onResetStore(() => {
  writeDefaults();
  return Promise.resolve();
});

const GC_INTERVAL_MS = 60_000;
if (typeof window !== 'undefined') {
  window.setInterval(() => {
    try {
      client.cache.gc();
    } catch (e) {
      // ignore during teardown
    }
  }, GC_INTERVAL_MS);
}

const groupsEnabledVar = makeVar(false);
groupsEnabledVar(localStorage.getItem('enabledGroups') === 'true');

export {loginHandler, groupsEnabledVar, client as default};
